/* eslint-disable jsx-a11y/anchor-has-content */
import React, { Ref } from 'react';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import NextLink from 'next/link';
import MuiLink, { LinkProps as MuiLinkProps } from '@mui/material/Link';

interface NextComposedProps {
  as?: string | object;
  href: string | { pathname?: string; [key: string]: unknown };
  ref?: Ref<HTMLAnchorElement>;
  [key: string]: unknown;
}

const NextComposed = React.forwardRef<HTMLAnchorElement, NextComposedProps>((props, ref) => {
  const { as, href, ...other } = props;

  return (
    <NextLink href={href as string} as={as as string} ref={ref} {...other} />
  );
});

NextComposed.displayName = 'NextComposed';

interface LinkProps extends Omit<MuiLinkProps, 'href'> {
  href: string | { pathname?: string; [key: string]: unknown };
  activeClassName?: string;
  innerRef?: Ref<HTMLAnchorElement>;
  naked?: boolean;
  prefetch?: boolean;
  children?: React.ReactNode;
}

function Link(props: LinkProps) {
  const {
    href,
    activeClassName = 'active',
    className: classNameProps,
    innerRef,
    naked,
    ...other
  } = props;

  const router = useRouter();
  const pathname = typeof href === 'string' ? href : href.pathname;
  const className = clsx(classNameProps, {
    [activeClassName]: router.pathname === pathname && activeClassName,
  });

  if (naked) {
    return (
      <NextComposed
        className={className}
        ref={innerRef}
        href={href}
        {...other}
      />
    );
  }

  return (
    <MuiLink
      component={NextComposed}
      className={className}
      ref={innerRef}
      href={href as string}
      {...other}
    />
  );
}

export default Link;
